import type { Annotation } from '../types.js';
import { SHADOW_ROOT_ID, API_ANNOTATIONS } from '../constants.js';
import { OVERLAY_STYLES } from './styles.js';
import { Highlighter } from './highlighter.js';
import { AnnotationForm } from './form.js';
import { PinManager } from './pin.js';
import { AnnotationPanel } from './panel.js';

export class Overlay {
  private host: HTMLElement;
  private shadowRoot: ShadowRoot;
  private highlighter: Highlighter;
  private form: AnnotationForm;
  private pinManager: PinManager;
  private panel: AnnotationPanel;
  private active = false;
  private escapeStack: ('annotate' | 'panel')[] = [];
  private devMode = !!(window as any).__ASTRO_ANNOTATE_DEV__;
  private annotations: Annotation[] = [];
  private scrollRafId: number | null = null;
  private onScroll: () => void;
  private onResize: () => void;

  constructor() {
    // Create host element
    this.host = document.createElement('div');
    this.host.id = SHADOW_ROOT_ID;
    document.body.appendChild(this.host);

    // Attach Shadow DOM
    this.shadowRoot = this.host.attachShadow({ mode: 'open' });

    // Inject styles
    const style = document.createElement('style');
    style.textContent = OVERLAY_STYLES;
    this.shadowRoot.appendChild(style);

    // Create components
    this.highlighter = new Highlighter(this.shadowRoot);
    this.form = new AnnotationForm(
      this.shadowRoot,
      () => this.onAnnotationCreated(),
      () => this.onFormClosed(),
      this.devMode,
    );
    this.pinManager = new PinManager(this.shadowRoot, () => this.loadAnnotations());
    let prevPanelVisible = false;
    this.panel = new AnnotationPanel(
      this.shadowRoot,
      () => this.loadAnnotations(),
      () => {
        this.renderPins();
        const nowVisible = this.panel.isVisible();
        if (nowVisible && !prevPanelVisible) {
          this.pushEscapeLayer('panel');
        } else if (!nowVisible && prevPanelVisible) {
          this.removeEscapeLayer('panel');
        }
        prevPanelVisible = nowVisible;
      },
    );

    // Wire up annotate button in FAB stack
    this.panel.setOnEnterAnnotationMode(() => {
      this.setActive(true);
      window.dispatchEvent(new CustomEvent('aa:state-changed', { detail: { active: true } }));
    });

    // Listen for toggle from Dev Toolbar
    window.addEventListener('aa:toggle', this.onToolbarToggle);

    // Load existing annotations
    this.loadAnnotations();

    // Close detail popup when clicking outside
    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (!this.host.contains(target)) {
        this.pinManager.hideDetail();
      }
    });

    // Update pin positions on scroll (rAF-throttled)
    this.onScroll = () => {
      if (this.scrollRafId === null) {
        this.scrollRafId = requestAnimationFrame(() => {
          this.pinManager.updatePositions();
          this.scrollRafId = null;
        });
      }
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.onResize = () => {
      this.pinManager.updatePositions();
    };
    window.addEventListener('resize', this.onResize, { passive: true });

    // Keyboard shortcuts
    document.addEventListener('keydown', this.onKeyDown);
  }

  private setActive(active: boolean): void {
    this.active = active;

    if (active) {
      this.pushEscapeLayer('annotate');
      this.form.hide();
      this.pinManager.hideDetail();
      this.panel.setAnnotationMode(true, () => {
        this.setActive(false);
        window.dispatchEvent(new CustomEvent('aa:state-changed', { detail: { active: false } }));
      });
      document.addEventListener('mousemove', this.highlighter.onMouseMove);
      document.addEventListener('click', this.onElementClick, true);
    } else {
      this.removeEscapeLayer('annotate');
      document.removeEventListener('mousemove', this.highlighter.onMouseMove);
      document.removeEventListener('click', this.onElementClick, true);
      this.highlighter.hide();
      this.form.hide();
      this.panel.setAnnotationMode(false);
    }
  }

  private onToolbarToggle = ((e: CustomEvent) => {
    this.setActive(e.detail.active);
  }) as EventListener;

  private onElementClick = (e: MouseEvent): void => {
    const target = e.target as Element;

    // Ignore clicks on our own overlay
    if (this.host.contains(target) || this.host === target) return;

    // Don't switch targets while form is open
    if (this.form.isVisible()) return;

    e.preventDefault();
    e.stopPropagation();

    // Hide highlight and show form
    this.highlighter.hide();
    document.removeEventListener('mousemove', this.highlighter.onMouseMove);

    this.form.show(target);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    // Don't intercept when user is typing in an external input
    const active = document.activeElement;
    const isExternalInput = active &&
      (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' ||
       (active as HTMLElement).isContentEditable) &&
      active !== this.host && !this.host.contains(active);

    // Alt+C: toggle annotation mode (Figma-inspired, e.code for layout-independence)
    if (e.altKey && e.code === 'KeyC' && !isExternalInput) {
      e.preventDefault();
      const newActive = !this.active;
      this.setActive(newActive);
      window.dispatchEvent(new CustomEvent('aa:state-changed', { detail: { active: newActive } }));
      return;
    }

    // Alt+L: toggle annotation panel
    if (e.altKey && e.code === 'KeyL' && !isExternalInput) {
      if (this.panel.isEditing()) return;
      e.preventDefault();
      this.panel.toggle();
      return;
    }

    // Escape: form always first, then stack-based priority (last opened closes first)
    if (e.key === 'Escape') {
      if (this.form.isVisible()) { this.form.hide(); return; }
      const top = this.escapeStack[this.escapeStack.length - 1];
      if (top === 'panel' && this.panel.isVisible()) {
        this.panel.hide();
        return;
      }
      if (top === 'annotate' && this.active) {
        this.setActive(false);
        window.dispatchEvent(new CustomEvent('aa:state-changed', { detail: { active: false } }));
        return;
      }
    }
  };

  private async loadAnnotations(): Promise<void> {
    try {
      const page = window.location.pathname;
      const res = await fetch(`${API_ANNOTATIONS}?page=${encodeURIComponent(page)}`);
      if (!res.ok) return;

      const data = await res.json();
      this.annotations = data.annotations || [];
      const openCount = this.annotations.filter((a) => a.status === 'open').length;
      window.dispatchEvent(new CustomEvent('aa:count', { detail: { count: openCount } }));
      this.renderPins();
      this.panel.update(this.annotations);
    } catch {
      // API not available yet, will retry
    }
  }

  private renderPins(): void {
    let panelSide: 'left' | 'right' | null = null;
    if (this.panel.isVisible() && this.panel.getMode() === 'docked') {
      panelSide = this.panel.getSide();
    }
    this.pinManager.render(this.annotations, panelSide);
  }

  private onAnnotationCreated(): void {
    this.loadAnnotations();
  }

  private onFormClosed(): void {
    // Re-enable highlighting if still in annotation mode
    if (this.active) {
      document.addEventListener('mousemove', this.highlighter.onMouseMove);
    }
  }

  private pushEscapeLayer(layer: 'annotate' | 'panel'): void {
    this.escapeStack = this.escapeStack.filter(l => l !== layer);
    this.escapeStack.push(layer);
  }

  private removeEscapeLayer(layer: 'annotate' | 'panel'): void {
    this.escapeStack = this.escapeStack.filter(l => l !== layer);
  }

  getPanelState(): Record<string, unknown> {
    return this.panel.getState();
  }

  restorePanelState(state: Record<string, unknown>): void {
    this.panel.restoreState(state as { visible: boolean; filter: string; side: string; mode?: string; floating?: { x: number; y: number; width: number; height: number } });
  }

  destroy(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('aa:toggle', this.onToolbarToggle);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
    }
    if (this.active) {
      window.dispatchEvent(new CustomEvent('aa:state-changed', { detail: { active: false } }));
    }
    this.setActive(false);
    this.highlighter.destroy();
    this.form.destroy();
    this.pinManager.destroy();
    this.panel.destroy();
    this.host.remove();
  }
}
